import {Component, inject, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {MatButton} from "@angular/material/button";
import {MatDivider} from "@angular/material/divider";
import {
  CampaignFilterData,
  CampaignsFilter,
  CampaignsFiltersComponent,
  CampaignUserStatus,
  Destroyable,
  MoneyFormatPipe
} from "shared-lib";
import {NgClass, NgOptimizedImage} from "@angular/common";
import {TranslocoDirective} from "@jsverse/transloco";
import {LoyaltyScreen} from '../../services/campaign.service';
import {UserOnboardingStatus} from '../../services/models/user-onboarding-status';
import {PlacesWithBenefitsComponent} from '../places-with-benefits/places-with-benefits.component';
import {UserOnboardingStatusService} from '../../services/user-onboarding-status.service';
import {UserCampaignService} from '../../services/user-campaign.service';
import {MatBottomSheet} from '@angular/material/bottom-sheet';
import {
  CampaignsRewardsListResultData,
  UserRewardsListSlideInComponent
} from '../user-rewards-list-slide-in/user-rewards-list-slide-in.component';
import {interval, startWith, takeUntil, takeWhile} from 'rxjs';
import {switchMap, tap} from 'rxjs/operators';
import {LocationsWithLoyaltyMapComponent} from '../map/locations-with-loyalty-map/locations-with-loyalty-map.component';
import {CampaignsFilterStore} from '../../store/campaigns-filter.store';
import {
  CampaignsWithoutLoyaltyListComponent
} from '../campaigns/campaigns-list-component/campaigns-without-loyalty-list/campaigns-without-loyalty-list.component';
import {
  LocationsWithoutLoyaltyMapComponent
} from '../map/locations-without-loyalty-map/locations-without-loyalty-map.component';
import {MyPlacesScreenComponent} from '../my-places-screen/my-places-screen.component';
import {GhostMerchantBountyListComponent} from '../ghost-merchant-bounty-list/ghost-merchant-bounty-list.component';
import {MatIcon} from '@angular/material/icon';

@Component({
  selector: 'app-loyalty-layout',
  imports: [
    TranslocoDirective,
    MatButton,
    MatDivider,
    MoneyFormatPipe,
    NgOptimizedImage,
    NgClass,
    MyPlacesScreenComponent,
    PlacesWithBenefitsComponent,
    LocationsWithLoyaltyMapComponent,
    LocationsWithoutLoyaltyMapComponent,
    CampaignsWithoutLoyaltyListComponent,
    GhostMerchantBountyListComponent,
    MatIcon
  ],
  templateUrl: './loyalty-layout.component.html',
  styleUrl: './loyalty-layout.component.scss',
})
export class LoyaltyLayoutComponent extends Destroyable {
  readonly filterStore = inject(CampaignsFilterStore);

  campaignsView = MobileCampaignsViewOptions;
  selectedCampaignView = MobileCampaignsViewOptions.LIST;

  userRewardsSum: number | undefined;
  isUserOnboardingDisplayed: boolean | undefined;
  isUserTransactionHistoryLoaded: boolean = false;
  selectedPlacesLoyaltyStatus: LoyaltyScreen = LoyaltyScreen.WITH_LOYALTY;
  protected readonly MobileCampaignsViewOptions = MobileCampaignsViewOptions;
  protected readonly CampaignUserStatus = CampaignUserStatus;
  protected readonly LoyaltyScreen = LoyaltyScreen;

  constructor(
    userOnboardingStatusService: UserOnboardingStatusService,
    userCampaignService: UserCampaignService,
    private bottomSheet: MatBottomSheet,
    private route: ActivatedRoute
  ) {
    super();
    this.initScreenFromQueryParam();
    interval(1000)
      .pipe(
        startWith(0),
        switchMap(() => userOnboardingStatusService.getUserOnboardingStatus()),
        tap((userOnboardingStatus: UserOnboardingStatus) => {
          this.isUserOnboardingDisplayed = userOnboardingStatus.loyaltyOnboardingDisplayed;
          this.isUserTransactionHistoryLoaded = userOnboardingStatus.transactionHistoryLoaded;
        }),
        takeWhile((userOnboardingStatus) => !userOnboardingStatus.transactionHistoryLoaded, true),
        takeUntil(this.destroy$)
      )
      .subscribe();

    userCampaignService.getUserRewardsSum().subscribe(result => {
      this.userRewardsSum = result.rewardsSum;
    })
  }

  filterByUserStatus(status: LoyaltyScreen): void {
    this.selectedPlacesLoyaltyStatus = status;
  }

  switchView(subMenuOption: MobileCampaignsViewOptions): void {
    this.selectedCampaignView = subMenuOption;
  }

  openRewardListSlideIn(): void {
    if (this.userRewardsSum) {
      const openedBottomSheet = this.bottomSheet.open(UserRewardsListSlideInComponent, {
        data: {rewardsSum: this.userRewardsSum}
      });

      openedBottomSheet.afterDismissed().subscribe((result: CampaignsRewardsListResultData) => {
        if (result && result.withdrawn) {
          this.userRewardsSum = undefined;
        }
      })
    }
  }

  openFilterBottomSlideIn(): void {
    const bottomSheetRef = this.bottomSheet.open(CampaignsFiltersComponent, {
      data: {
        state: this.filterStore.filter()
      } as CampaignFilterData
    });

    bottomSheetRef.afterDismissed().subscribe((campaignsFilter) => {
      this.filterStore.updateValue({...campaignsFilter, geoBounds: this.filterStore.filter().geoBounds});
    });
  }

  private initScreenFromQueryParam(): void {
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const screen = params['screen'];
      if (screen) {
        switch (screen) {
          case 'my-places':
            this.selectedPlacesLoyaltyStatus = LoyaltyScreen.WITH_LOYALTY;
            break;
          case 'discover':
            this.selectedPlacesLoyaltyStatus = LoyaltyScreen.WITHOUT_LOYALTY;
            break;
          case 'bounties':
            this.selectedPlacesLoyaltyStatus = LoyaltyScreen.BOUNTIES;
            break;
        }
      }
    });
  }
}

export enum MobileCampaignsViewOptions {
  LIST = 'LIST',
  MAP = 'MAP',
}

export interface PlacesFilter {
  sort: SortOptions,
  categoryId?: string,
  merchantId?: string,
  loyaltyScore?: NumericRange,
  amount?: NumericRange,
}

export enum SortOptions {
  NEAREST = "NEAREST",
  HIGHEST_AMOUNT = "HIGHEST_AMOUNT",
  MOST_URGENT = "MOST_URGENT",
  MOST_RECENT = "MOST_RECENT",
}

export interface NumericRange {
  min: number;
  max?: number;
}
